import { db } from '@/lib/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  query, 
  where, 
  getDocs,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';

export interface DailyCommitment {
  id: string;
  userId: string;
  date: string; // YYYY-MM-DD
  loginFiles: number;
  loginAmount: number;
  disbursementFiles: number;
  disbursementAmount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface MonthlyCommitment {
  id: string;
  userId: string;
  month: string; // YYYY-MM
  loginFiles: number;
  loginAmount: number;
  disbursementFiles: number;
  disbursementAmount: number;
  createdAt: Date;
  updatedAt: Date;
}

const DAILY_COMMITMENTS_COLLECTION = 'dailyCommitments';
const MONTHLY_COMMITMENTS_COLLECTION = 'monthlyCommitments';

// Helper to safely convert any date format to Date object
function toDate(val: any): Date {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (typeof val === 'number') return new Date(val);
  if (typeof val === 'string') return new Date(val);
  return new Date();
}

// Helper to convert Firestore data to DailyCommitment
function convertFirestoreToDailyCommitment(id: string, data: any): DailyCommitment {
  return {
    id,
    userId: data.userId,
    date: data.date,
    loginFiles: data.loginFiles || 0,
    loginAmount: data.loginAmount || 0,
    disbursementFiles: data.disbursementFiles || 0,
    disbursementAmount: data.disbursementAmount || 0,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

// Helper to convert Firestore data to MonthlyCommitment
function convertFirestoreToMonthlyCommitment(id: string, data: any): MonthlyCommitment {
  return {
    id,
    userId: data.userId,
    month: data.month,
    loginFiles: data.loginFiles || 0,
    loginAmount: data.loginAmount || 0,
    disbursementFiles: data.disbursementFiles || 0,
    disbursementAmount: data.disbursementAmount || 0,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

// Daily Commitment Functions

/**
 * Get daily commitment for a specific user and date
 */
export async function getDailyCommitment(
  userId: string, 
  date: string // YYYY-MM-DD
): Promise<DailyCommitment | null> {
  try {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, DAILY_COMMITMENTS_COLLECTION, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertFirestoreToDailyCommitment(docSnap.id, docSnap.data());
    }
    return null;
  } catch (error) {
    console.error('Error getting daily commitment:', error);
    throw error;
  }
}

/**
 * Save or update daily commitment
 */
export async function saveDailyCommitment(
  userId: string,
  date: string, // YYYY-MM-DD
  commitment: {
    loginFiles: number;
    loginAmount: number;
    disbursementFiles: number;
    disbursementAmount: number;
  }
): Promise<DailyCommitment> {
  try {
    const docId = `${userId}_${date}`;
    const docRef = doc(db, DAILY_COMMITMENTS_COLLECTION, docId);
    
    // Check if document exists
    const existingDoc = await getDoc(docRef);
    
    const data = {
      userId,
      date,
      loginFiles: commitment.loginFiles,
      loginAmount: commitment.loginAmount,
      disbursementFiles: commitment.disbursementFiles,
      disbursementAmount: commitment.disbursementAmount,
      createdAt: existingDoc.exists() ? existingDoc.data().createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    await setDoc(docRef, data);
    
    return convertFirestoreToDailyCommitment(docId, {
      ...data,
      createdAt: data.createdAt instanceof Object && 'toDate' in data.createdAt ? data.createdAt.toDate() : new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error('Error saving daily commitment:', error);
    throw error;
  }
}

/**
 * Get all daily commitments for a user in a specific month
 */
export async function getDailyCommitmentsForMonth(
  userId: string,
  month: string // YYYY-MM
): Promise<DailyCommitment[]> {
  try {
    const q = query(
      collection(db, DAILY_COMMITMENTS_COLLECTION),
      where('userId', '==', userId),
      where('date', '>=', `${month}-01`),
      where('date', '<=', `${month}-31`)
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => 
      convertFirestoreToDailyCommitment(doc.id, doc.data())
    );
  } catch (error) {
    console.error('Error getting daily commitments for month:', error);
    throw error;
  }
}

// Monthly Commitment Functions

/**
 * Get monthly commitment for a specific user and month
 */
export async function getMonthlyCommitment(
  userId: string,
  month: string // YYYY-MM
): Promise<MonthlyCommitment | null> {
  try {
    const docId = `${userId}_${month}`;
    const docRef = doc(db, MONTHLY_COMMITMENTS_COLLECTION, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return convertFirestoreToMonthlyCommitment(docSnap.id, docSnap.data());
    }
    return null;
  } catch (error) {
    console.error('Error getting monthly commitment:', error);
    throw error;
  }
}

/**
 * Save or update monthly commitment
 */
export async function saveMonthlyCommitment(
  userId: string,
  month: string, // YYYY-MM
  commitment: {
    loginFiles: number;
    loginAmount: number;
    disbursementFiles: number;
    disbursementAmount: number;
  }
): Promise<MonthlyCommitment> {
  try {
    const docId = `${userId}_${month}`;
    const docRef = doc(db, MONTHLY_COMMITMENTS_COLLECTION, docId);
    
    // Check if document exists
    const existingDoc = await getDoc(docRef);
    
    const data = {
      userId,
      month,
      loginFiles: commitment.loginFiles,
      loginAmount: commitment.loginAmount,
      disbursementFiles: commitment.disbursementFiles,
      disbursementAmount: commitment.disbursementAmount,
      createdAt: existingDoc.exists() ? existingDoc.data().createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    await setDoc(docRef, data);
    
    return convertFirestoreToMonthlyCommitment(docId, {
      ...data,
      createdAt: data.createdAt instanceof Object && 'toDate' in data.createdAt ? data.createdAt.toDate() : new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    console.error('Error saving monthly commitment:', error);
    throw error;
  }
}

/**
 * Check if monthly commitment can be edited
 * Returns true if within the first 5 days of the month OR no commitment exists yet
 */
export function isMonthlyCommitmentEditable(existingCommitment: MonthlyCommitment | null): boolean {
  const today = new Date();
  const dayOfMonth = today.getDate();
  
  // Editable within first 5 days of month OR if not submitted yet
  return dayOfMonth <= 5 || !existingCommitment;
}

/**
 * Get current date in YYYY-MM-DD format
 */
export function getTodayDate(): string {
  return new Date().toISOString().split('T')[0];
}

/**
 * Get current month in YYYY-MM format
 */
export function getCurrentMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

/**
 * Get all daily commitments for a specific date (all users)
 */
export async function getAllDailyCommitments(date: string): Promise<DailyCommitment[]> {
  try {
    const q = query(
      collection(db, DAILY_COMMITMENTS_COLLECTION),
      where('date', '==', date)
    );
    
    const querySnapshot = await getDocs(q);
    const commitments: DailyCommitment[] = [];
    
    querySnapshot.forEach((doc) => {
      commitments.push(convertFirestoreToDailyCommitment(doc.id, doc.data()));
    });
    
    return commitments;
  } catch (error) {
    console.error('Error fetching all daily commitments:', error);
    return [];
  }
}

/**
 * Get all daily commitments for a date range (all users) - for attendance tracking
 */
export async function getDailyCommitments(
  startDate: string, 
  endDate: string
): Promise<DailyCommitment[]> {
  try {
    const q = query(
      collection(db, DAILY_COMMITMENTS_COLLECTION),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );
    
    const querySnapshot = await getDocs(q);
    const commitments: DailyCommitment[] = [];
    
    querySnapshot.forEach((doc) => {
      commitments.push(convertFirestoreToDailyCommitment(doc.id, doc.data()));
    });
    
    return commitments;
  } catch (error) {
    console.error('Error fetching daily commitments range:', error);
    return [];
  }
}

/**
 * Get all monthly commitments for a specific month (all users)
 */
export async function getAllMonthlyCommitments(month: string): Promise<MonthlyCommitment[]> {
  try {
    const q = query(
      collection(db, MONTHLY_COMMITMENTS_COLLECTION),
      where('month', '==', month)
    );
    
    const querySnapshot = await getDocs(q);
    const commitments: MonthlyCommitment[] = [];
    
    querySnapshot.forEach((doc) => {
      commitments.push(convertFirestoreToMonthlyCommitment(doc.id, doc.data()));
    });
    
    return commitments;
  } catch (error) {
    console.error('Error fetching all monthly commitments:', error);
    return [];
  }
}
